import type { Domain } from 'auth-common/domain';

export namespace AuthInfo {
  // @ts-ignore vite対策
  const _ = '';

  export type AuthRequirements = Domain.Object.AuthRequirements;

  export type ActiveAccountGrant = {
    id: Domain.Object.Account.Grant.Id;
    expiresAt: Domain.Object.Account.Grant.Effective.Ephemeral['expiresAt'];
    reason?: string | undefined;
  };

  export type Account = {
    id: Domain.Object.Account['id'];
    name: Domain.Object.Account['name'];
    email: Domain.Object.Account['email'];
    profileImageSrc: Domain.Object.Account['profileImageSrc'];
    /**
     * アクティブなシステム管理者権限を表す、既存互換の alias。
     *
     * 新しいコードでは、eligible と active permission の違いが明確になるように
     * `hasActiveSystemAdmin` を優先すること。
     */
    isSystemAdmin: Domain.Object.Account['isSystemAdmin'];
    isEmployee: boolean;
    /** システム管理者権限の有効化を申請できる状態。アクティブなシステム管理者権限ではない。 */
    isSystemAdminEligible: boolean;
    /** 表示・期限表示・失効操作用のアクティブなシステム管理者グラント情報。権限判定では hasActiveSystemAdmin を使うこと。 */
    activeSystemAdminGrant?: ActiveAccountGrant;
    /** 利用側フロントエンドでの、アクティブなシステム管理者権限の source of truth。 */
    hasActiveSystemAdmin: boolean;
    /** PII アクセスの有効化を申請できる状態。アクティブな PII アクセスではない。 */
    isPiiEligible: boolean;
    /** 表示・期限表示・失効操作用のアクティブな PII グラント情報。この互換 API はグループポリシー由来の PII 権限を返さない。 */
    activePiiAccessGrant?: ActiveAccountGrant;
    /** アカウント単位の一時 PII グラントが有効かどうか。プロダクト別の PII 権限判定には productKey 付きの internal-api getProjectAuthInfo を使うこと。 */
    hasActivePiiAccess: boolean;
    isActive: boolean;
  };

  export type Project = {
    id: Domain.Object.Project['id'];
    publicKey?: Domain.Object.Project['publicKey'];
    name: Domain.Object.Project['name'];
    productSettings: Domain.Object.Project['productSettings'];
    group: {
      id: Domain.Object.Group.Id;
      name: Domain.Object.Group['name'];
      policy: Domain.Object.Group.Policy;
      isProjectAdmin: boolean;
    } | null;
    /** 互換 API 用の一時 PII グラント判定。グループポリシー由来のプロダクト別 PII 権限判定には使わないこと。 */
    hasPiiAccessNow: boolean;
    authRequirements: AuthRequirements;
  };

  export type ProjectAccess =
    | {
        kind: 'membership';
      }
    | {
        kind: 'system_admin_product_entry';
        activeGrantId?: Domain.Object.Account.Grant.Id;
        expiresAt?: Domain.Object.Account.Grant.Effective.Ephemeral['expiresAt'];
      };

  export type RequestedProjectContext = {
    organization: {
      id: Domain.Object.Organization['id'];
      name: Domain.Object.Organization['name'];
      profileImageSrc: Domain.Object.Organization['profileImageSrc'];
      membershipRole?: {
        key: Domain.Object.Organization['roles'][number]['key'];
        name: Domain.Object.Organization['roles'][number]['name'];
      };
      authRequirements: AuthRequirements;
    };
    project: {
      id: Domain.Object.Project['id'];
      name: Domain.Object.Project['name'];
      productSettings: Domain.Object.Project['productSettings'];
      featureFlags: Domain.Object.Project['featureFlags'];
      authRequirements: AuthRequirements;
      membership: {
        group?: Project['group'];
      } | null;
      access: ProjectAccess;
      hasPiiAccessNow: boolean;
    };
  };

  /**
   * 現在選択中の project。
   *
   * `Organization.projects` は所属プロジェクト一覧のままにして、選択中 project は
   * 所属有無にかかわらず `RequestedProjectContext.project` と同じ形で扱う。
   * 所属している場合だけ `membership` が入る。所属メンバーでも権限グループが未設定なら
   * `membership.group` は存在しないため、所属判定には `access.kind` を使う。
   */
  export type SelectedProject = Omit<RequestedProjectContext['project'], 'featureFlags'> & {
    publicKey?: Project['publicKey'];
    featureFlags?: RequestedProjectContext['project']['featureFlags'];
  };

  export const ToSelectedProject = (project: Project): SelectedProject => ({
    id: project.id,
    ...(project.publicKey ? { publicKey: project.publicKey } : {}),
    name: project.name,
    productSettings: project.productSettings,
    authRequirements: project.authRequirements,
    membership: {
      ...(project.group ? { group: project.group } : {}),
    },
    access: { kind: 'membership' },
    hasPiiAccessNow: project.hasPiiAccessNow,
  });

  export type Organization = {
    id: Domain.Object.Organization['id'];
    name: Domain.Object.Organization['name'];
    profileImageSrc: Domain.Object.Organization['profileImageSrc'];
    role?: {
      key: Domain.Object.Organization['roles'][number]['key'];
      name: Domain.Object.Organization['roles'][number]['name'];
    };
    authRequirements: AuthRequirements;
    projects: Project[];
  };
}

export type AuthInfo = {
  account: AuthInfo.Account;
  organizations: AuthInfo.Organization[];
  requestedProjectContext?: AuthInfo.RequestedProjectContext;
};
